/** On-demand speed test with recent history. Moved from `pages/network.tsx`
 *  when the page split into tabs (#310); renders on the Performance tab. */
import { useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Gauge } from "lucide-react";
import { toast } from "sonner";
import { Badge, Box, Button, Card, Divider, Group, Stack, Text } from "@mantine/core";
import { tenantApi, SpeedtestHistoryItem } from "@/lib/api/client";
import { getConfig } from "@/lib/config";
import { useNetworkStatus } from "@/context/network-context";
import { useSpeedtestHistory } from "@/hooks/use-network-queries";

const SIDECAR_URL = getConfig().SIDECAR_BASE_URL;

function SpeedtestHistoryRow({ item }: { item: SpeedtestHistoryItem }) {
  const date = new Date(item.created_at);
  const timeStr = date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const hasResults = item.download_mbps != null || item.upload_mbps != null;

  return (
    <Box py="xs">
      <Group justify="space-between" gap={4}>
        <Text fz="xs" c="dimmed">
          {timeStr}
          {item.created_by_name && <Text component="span" ml={6} style={{ opacity: 0.7 }}>&bull; {item.created_by_name}</Text>}
        </Text>
        <a
          href={item.speedtest_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 2, color: "var(--mantine-color-dimmed)" }}
          aria-label="View full report"
        >
          <ExternalLink size={12} />
        </a>
      </Group>
      {hasResults ? (
        <Group gap="sm" mt={2}>
          {item.download_mbps != null && (
            <Text fz="xs" fw={500} c="blue.6" style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ArrowDown size={12} />
              {item.download_mbps.toFixed(1)} Mbps
            </Text>
          )}
          {item.upload_mbps != null && (
            <Text fz="xs" fw={500} c="green.6" style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ArrowUp size={12} />
              {item.upload_mbps.toFixed(1)} Mbps
            </Text>
          )}
          {item.ping_ms != null && (
            <Text fz="xs" c="dimmed">
              {item.ping_ms.toFixed(0)} ms ping
            </Text>
          )}
          {item.jitter_ms != null && (
            <Text fz="xs" c="dimmed">
              +/-{item.jitter_ms.toFixed(0)} ms jitter
            </Text>
          )}
        </Group>
      ) : (
        <Badge variant="light" size="sm" mt={4} tt="capitalize">
          {item.status}
        </Badge>
      )}
    </Box>
  );
}

export function SpeedTestCard() {
  const { status } = useNetworkStatus();
  const isRemote = status === "external";
  const [isCreating, setIsCreating] = useState(false);

  const { data: historyData } = useSpeedtestHistory();

  const history = historyData ?? [];

  const handleStartSpeedtest = async () => {
    setIsCreating(true);
    try {
      let ipToken: string | undefined;
      try {
        const response = await fetch(`${SIDECAR_URL}/signed-ip-info`, { mode: "cors" });
        if (response.ok) {
          const data = await response.json();
          ipToken = data.token;
        }
      } catch {
        // Sidecar unreachable -- proceed without IP token
      }

      const response = await tenantApi.createDiagnosticSession(ipToken);
      const { speedtest_url } = response.data;
      window.open(speedtest_url, "_blank");
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || "Failed to start speed test");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Card withBorder padding="lg">
      <Group gap="sm" mb="md">
        <Box p={8} style={{ background: "var(--mantine-primary-color-light)", borderRadius: 8 }}>
          <Gauge size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
        </Box>
        <div>
          <Text fw={500}>Speed Test</Text>
          <Text fz="xs" c="dimmed">Measure your download and upload speeds</Text>
        </div>
      </Group>
      <Stack gap="md">
        {isRemote ? (
          <Text fz="sm" c="dimmed" ta="center" py="xs">Speed test is only available when connected on-site</Text>
        ) : (
          <>
            <Button onClick={handleStartSpeedtest} disabled={isCreating} fullWidth loading={isCreating}>
              <ExternalLink size={16} style={{ marginRight: 4 }} />
              Start Speed Test
            </Button>
            <Text fz="xs" c="dimmed" ta="center">Opens in a new tab</Text>
          </>
        )}

        {history.length > 0 && (
          <>
            <Divider />
            <Text fz="xs" fw={500} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Recent tests</Text>
            <Stack gap={0} style={{ borderTop: "none" }}>
              {history.slice(0, 5).map((item, i) => (
                <Box key={item.ref} style={i > 0 ? { borderTop: "1px solid var(--mantine-color-default-border)" } : undefined}>
                  <SpeedtestHistoryRow item={item} />
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}
